import { preparationDepuisTache, tacheCreation } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ErreurApi, type FicheProjet, lire, requetes } from '../api'
import {
  LIBELLES_ISSUE_ENVOI,
  LIBELLES_STATUT_BROUILLON,
  LIBELLES_TYPE_BROUILLON,
} from '../libelles'
import { EnvoiDansLaTache } from '../pages/BoiteEnvoi'
import { Formulaire } from './Formulaire'
import { Icone } from './Icone'
import { Vide } from './Section'

// Onglet « Envois » d'un projet : les courriels, courriers, consignes d'agent et rendez-vous de
// ses tâches (chacun s'ouvre ici, comme dans sa tâche), les courriels que ses tâches proposent,
// et un nouveau courriel, préparé pour une de ses tâches ou pour une tâche créée dans le projet.

const OUVERTES = ['a_faire', 'en_cours', 'en_attente_tiers']

export function EnvoisProjet({ projet }: { projet: FicheProjet }) {
  const client = useQueryClient()
  const envois = useQuery(requetes.brouillons({ projetId: projet.id, vue: 'tous' }))
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [nouveau, setNouveau] = useState(false)
  const [redaction, setRedaction] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const proposes = projet.taches.filter((t) => t.courrielPropose)
  const ouvertes = projet.taches.filter((t) => OUVERTES.includes(t.statut))

  /** Prépare un courriel pour une tâche du projet (Claude le rédige) et l'ouvre ici. */
  const preparer = async (tacheId: string, v: Record<string, unknown>) => {
    const b = await lire(
      api.taches[':id'].preparer.$post({
        param: { id: tacheId },
        json: corps(preparationDepuisTache, { type: 'courriel', ...v }),
      }),
    )
    await Promise.all([
      client.invalidateQueries({ queryKey: ['brouillons'] }),
      client.invalidateQueries({ queryKey: ['projets', 'fiche', projet.id] }),
    ])
    setOuvert(b.id)
  }

  return (
    <div className="envois-projet">
      {projet.ecriture && (
        <div className="ligne-actions">
          <button
            type="button"
            className="bouton petit"
            onClick={() => setNouveau(!nouveau)}
            aria-expanded={nouveau}
          >
            <Icone nom="forward_to_inbox" />
            Nouveau courriel
          </button>
        </div>
      )}
      {nouveau && (
        <Formulaire
          champs={[
            {
              nom: 'tacheId',
              libelle: 'Pour la tâche',
              type: 'choix',
              options: ouvertes.map((t) => ({ valeur: t.id, libelle: t.titre })),
              aide: 'Vide : une nouvelle tâche du projet, au titre ci-dessous.',
            },
            { nom: 'titre', libelle: 'Nouvelle tâche (titre)', type: 'texte' },
            {
              nom: 'objectif',
              libelle: 'Ce que le courriel doit dire',
              type: 'texte',
              aide: 'Facultatif : Claude le rédige d’après la tâche et son mail.',
            },
          ]}
          libelleBouton="Rédiger avec Claude"
          onAnnuler={() => setNouveau(false)}
          onValider={async (v) => {
            let tacheId = v.tacheId as string | null
            if (!tacheId) {
              if (!v.titre)
                throw new ErreurApi('Choisir une tâche, ou donner le titre d’une nouvelle.', 400)
              tacheId = (
                await lire(
                  api.taches.$post({
                    json: corps(tacheCreation, { titre: v.titre, projetId: projet.id }),
                  }),
                )
              ).id
            }
            await preparer(tacheId, v.objectif ? { objectif: v.objectif } : {})
            setNouveau(false)
          }}
        />
      )}
      {projet.ecriture && proposes.length > 0 && (
        <div className="envois-proposes">
          <h3>Courriels proposés par les tâches</h3>
          {proposes.map((t) => (
            <div key={t.id} className="ligne">
              <div className="ligne-contenu">
                <strong>{t.courrielPropose!.libelle}</strong>
                <span className="discret">
                  <Link to="/taches/$id" params={{ id: t.id }}>
                    {t.titre}
                  </Link>{' '}
                  · {t.courrielPropose!.a}
                </span>
              </div>
              <div className="ligne-actions">
                <button
                  type="button"
                  className="bouton-discret"
                  disabled={redaction !== null}
                  onClick={async () => {
                    setErreur(null)
                    setRedaction(t.id)
                    try {
                      await preparer(t.id, {
                        motif: t.courrielPropose!.motif,
                        a: t.courrielPropose!.a,
                      })
                    } catch (e) {
                      setErreur(e instanceof ErreurApi ? e.message : 'Rédaction impossible.')
                    } finally {
                      setRedaction(null)
                    }
                  }}
                >
                  <Icone nom="auto_awesome" />
                  {redaction === t.id ? 'Rédaction…' : 'Rédiger'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {envois.isPending && <Vide>Chargement…</Vide>}
      {envois.data?.length === 0 && <Vide>Aucun envoi pour les tâches de ce projet.</Vide>}
      {envois.data?.map((b) => (
        <div key={b.id} className="envoi-projet">
          <button
            type="button"
            className="ligne ligne-lien"
            aria-expanded={ouvert === b.id}
            onClick={() => setOuvert(ouvert === b.id ? null : b.id)}
          >
            <div className="ligne-contenu">
              <span>
                <strong>{b.objet}</strong>{' '}
                <span className="badge">{LIBELLES_TYPE_BROUILLON[b.type]}</span>
                <span className="badge">{LIBELLES_STATUT_BROUILLON[b.statut]}</span>
                {b.dansGmail && b.statut !== 'fait' && <span className="badge">Dans Gmail</span>}
              </span>
              <span className="discret">
                {[
                  b.destinataireTiers ?? b.destinataire,
                  b.tache && `tâche « ${b.tache} »`,
                  b.issueTache &&
                    b.issueTache !== 'rien' &&
                    b.statut !== 'fait' &&
                    `quand il part : ${LIBELLES_ISSUE_ENVOI[b.issueTache].toLowerCase()}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            <Icone nom={ouvert === b.id ? 'keyboard_arrow_down' : 'chevron_right'} />
          </button>
          {ouvert === b.id && <EnvoiDansLaTache id={b.id} avecTache />}
        </div>
      ))}
    </div>
  )
}
